import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/auth';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface LowStockItem {
  variantId:   string;
  productTitle:string;
  variantTitle:string;
  available:   number;
  onHand:      number;
  committed:   number;
  sku:         string | null;
}

export default function InventoryPage() {
  const [items,   setItems]   = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ data: LowStockItem[] }>('/inventory/low-stock?threshold=10')
      .then(r => setItems(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Inventory</h1>
          <p style={styles.pageSubtitle}>Low stock alerts</p>
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingWrap}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--admin-text-muted)' }} /></div>
      ) : items.length === 0 ? (
        <div style={styles.emptyState}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.5" style={{ marginBottom: 12 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <p style={styles.emptyTitle}>All stock levels are healthy.</p>
          <p style={styles.emptyBody}>No variants below the threshold.</p>
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Product / Variant</th>
                <th style={styles.th}>SKU</th>
                <th style={styles.th}>On Hand</th>
                <th style={styles.th}>Committed</th>
                <th style={styles.th}>Available</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const isOut = item.available <= 0;
                return (
                  <tr key={item.variantId} style={styles.tr}>
                    <td style={styles.td}>
                      <p style={styles.productName}>{item.productTitle}</p>
                      <p style={styles.variantName}>{item.variantTitle}</p>
                    </td>
                    <td style={styles.td}><span style={styles.sku}>{item.sku ?? '—'}</span></td>
                    <td style={styles.td}><span style={styles.num}>{item.onHand}</span></td>
                    <td style={styles.td}><span style={styles.num}>{item.committed}</span></td>
                    <td style={styles.td}>
                      <span style={{ ...styles.num, color: isOut ? 'var(--admin-error)' : 'var(--admin-warning)', fontWeight: 700 }}>
                        {item.available}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={isOut
                        ? { ...styles.badge, background: 'var(--admin-error-bg)', color: 'var(--admin-error)' }
                        : { ...styles.badge, background: 'var(--admin-warning-bg)', color: 'var(--admin-warning)' }
                      }>
                        {isOut ? 'Out of stock' : 'Low stock'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page:        { maxWidth: 1200, margin: '0 auto' },
  pageHeader:  { display: 'flex', justifyContent: 'space-between', marginBottom: 24 },
  pageTitle:   { fontSize: 22, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2 },
  pageSubtitle:{ fontSize: 12, color: 'var(--admin-text-muted)' },
  loadingWrap: { display: 'flex', justifyContent: 'center', padding: 48 },
  emptyState:  { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 64, color: 'var(--admin-success)', textAlign: 'center' },
  emptyTitle:  { fontSize: 16, fontWeight: 600, color: 'var(--admin-text)' },
  emptyBody:   { fontSize: 13, color: 'var(--admin-text-muted)', marginTop: 6 },
  tableWrap:   { background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '11px 16px', textAlign: 'left' as const, fontSize: 11, fontWeight: 600, color: 'var(--admin-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--admin-border)', background: 'var(--admin-surface-2)' },
  tr:          { borderBottom: '1px solid var(--admin-border-light)' },
  td:          { padding: '12px 16px', verticalAlign: 'middle' as const, fontSize: 13 },
  productName: { fontWeight: 500, color: 'var(--admin-text)' },
  variantName: { fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 2 },
  sku:         { fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--admin-text-muted)', background: 'var(--admin-surface-2)', padding: '2px 6px', borderRadius: 4 },
  num:         { fontWeight: 500, color: 'var(--admin-text)' },
  badge:       { display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
};
